/**
 * Les adresses de l'administration : une table, lue par `App.tsx`, par les
 * menus, par les liens des pages et par les tests.
 *
 * ## Quatre parties
 *
 * Décidé par Éclosion le 27/09/2026 : un administrateur qui se connecte
 * arrive sur le tableau de bord, et l'administration a quatre parties.
 *
 * - `/admin` — **Tableau de bord** : les informations générales, et les
 *   raccourcis vers les pages qui les traitent ;
 * - `/admin/comptabilite/…` — **Comptabilité** : la gestion comptable ;
 * - `/admin/planification/…` — **Planification** : ce que l'administration
 *   contenait déjà — stages, programmation, parascolaire, tarifs, demandes,
 *   utilisateurs… ;
 * - `/staff/*` — **Gestion** : l'espace de gestion quotidienne des centres,
 *   inchangé, hors de cette table.
 *
 * `/admin/users/…` reste **un seul module** (décidé le 27/09/2026 : ne pas le
 * séparer). Il est rangé dans Planification › Administration ; son accueil
 * porte deux tuiles : les données de l'utilisateur, et ses données
 * financières. Comptabilité › Clients mène à la seconde.
 *
 * ## Les anciennes adresses
 *
 * Elles redirigent, en un seul saut, vers l'adresse canonique, en gardant la
 * chaîne de requête : `/admin/factures?q=CDV-26-…` doit ouvrir la recherche,
 * pas l'écran vide. Un `<Navigate>` nu la perdrait (cartographie du Lot UI 0,
 * § 5 et § 10).
 */

export const CHEMINS = {
  tableauDeBord: '/admin',

  comptabilite: {
    accueil: '/admin/comptabilite',
    rapprochement: '/admin/comptabilite/banque/rapprochement',
    attente: '/admin/comptabilite/banque/compte-d-attente',
    factures: '/admin/comptabilite/clients/factures',
    attestations: '/admin/comptabilite/clients/attestations-fiscales',
    controles: '/admin/comptabilite/rapports/controles',
    reprise: '/admin/comptabilite/cloture/reprise-ouverture',
    journaux: '/admin/comptabilite/ecritures/journaux',
    ecritures: '/admin/comptabilite/ecritures/liste',
    /* Une fiche vit sous sa liste : elle en allume l'entrée de menu. */
    ecriture: '/admin/comptabilite/ecritures/liste/:ecritureId',
  },

  planification: {
    accueil: '/admin/planification',
    centres: '/admin/planification/vacances/centres',
    stages: '/admin/planification/vacances/stages',
    programmation: '/admin/planification/vacances/programmation',
    inscriptions: '/admin/planification/vacances/inscriptions',
    groupes: '/admin/planification/vacances/groupes',
    listeAttente: '/admin/planification/vacances/liste-attente',
    annulations: '/admin/planification/demandes/annulations',
    inclusion: '/admin/planification/demandes/inclusion',
    bonsCadeaux: '/admin/planification/administration/bons-cadeaux',
    ecoles: '/admin/planification/administration/ecoles',
    tarifs: '/admin/planification/administration/tarifs',
    newsletter: '/admin/planification/administration/newsletter',
    parametresSite: '/admin/planification/administration/parametres-site',
    /* Les journées pédagogiques ; une journée vit sous sa liste, et l'allume. */
    garderieFacturation: '/admin/planification/garderie/facturation',
    jpa: '/admin/planification/journees-pedagogiques',
    jpaProgrammer: '/admin/planification/journees-pedagogiques/programmer',
    jpaModifier: '/admin/planification/journees-pedagogiques/:sessionId/modifier',
    jpaListes: '/admin/planification/journees-pedagogiques/:sessionId/listes',
    jpaGroupes: '/admin/planification/journees-pedagogiques/:sessionId/groupes',
    /* Un centre : sa création, puis sa fiche — usage, école et classes comprises. */
    centreNouveau: '/admin/planification/vacances/centres/nouveau',
    centreFiche: '/admin/planification/vacances/centres/:centreId',
  },

  utilisateurs: {
    accueil: '/admin/users',
    donnees: '/admin/users/donnees',
    finances: '/admin/users/finances',
    fiche: '/admin/users/:userId',
    financesDuCompte: '/admin/users/:userId/finances',
    edition: '/admin/users/:userId/edition',
  },
} as const;

// ---------------------------------------------------------------------------
//  Les liens paramétrés
// ---------------------------------------------------------------------------

const avecId = (motif: string, userId: string) => motif.replace(':userId', encodeURIComponent(userId));

export const ficheUtilisateur = (userId: string) => avecId(CHEMINS.utilisateurs.fiche, userId);
export const financesDuCompte = (userId: string) => avecId(CHEMINS.utilisateurs.financesDuCompte, userId);
export const editionUtilisateur = (userId: string) => avecId(CHEMINS.utilisateurs.edition, userId);
export const ficheEcriture = (ecritureId: string) =>
  CHEMINS.comptabilite.ecriture.replace(':ecritureId', encodeURIComponent(ecritureId));
export const modifierJournee = (sessionId: string) =>
  CHEMINS.planification.jpaModifier.replace(':sessionId', encodeURIComponent(sessionId));
export const listesDeLaJournee = (sessionId: string) =>
  CHEMINS.planification.jpaListes.replace(':sessionId', encodeURIComponent(sessionId));
export const groupesDeLaJournee = (sessionId: string) =>
  CHEMINS.planification.jpaGroupes.replace(':sessionId', encodeURIComponent(sessionId));
/** La fiche d'un centre ; `ancre` ouvre une section (`classes`, `ecole`). */
export const ficheDuCentre = (centreId: string, ancre?: 'classes' | 'ecole') =>
  CHEMINS.planification.centreFiche.replace(':centreId', encodeURIComponent(centreId)) + (ancre ? `#${ancre}` : '');

/** Une liste ouverte sur une recherche : `?q=…`, lu par la page d'arrivée. */
export function avecRecherche(chemin: string, q: string | null | undefined): string {
  const t = (q ?? '').trim();
  return t === '' ? chemin : `${chemin}?q=${encodeURIComponent(t)}`;
}

// ---------------------------------------------------------------------------
//  Les redirections
// ---------------------------------------------------------------------------

export interface Redirection {
  /** L'ancienne adresse, telle qu'un favori ou un courriel la porte encore. */
  de: string;
  /** L'adresse canonique, atteinte en un seul saut. */
  vers: string;
}

const C = CHEMINS.comptabilite;
const P = CHEMINS.planification;

export const REDIRECTIONS: readonly Redirection[] = [
  // Comptabilité
  { de: '/admin/rapprochement', vers: C.rapprochement },
  { de: '/admin/bank-transactions', vers: C.rapprochement },
  { de: '/admin/factures', vers: C.factures },
  { de: '/admin/payments', vers: C.factures },
  { de: '/admin/credit-notes', vers: C.factures },
  { de: '/admin/attestations', vers: C.attestations },
  { de: '/admin/financial-overview', vers: C.controles },
  { de: '/admin/reprise-ouverture', vers: C.reprise },

  // Planification
  { de: '/admin/centers', vers: P.centres },
  { de: '/admin/stages', vers: P.stages },
  { de: '/admin/sessions', vers: P.programmation },
  { de: '/admin/inscriptions', vers: P.inscriptions },
  { de: '/admin/registrations-by-group', vers: P.groupes },
  { de: '/admin/waiting-list', vers: P.listeAttente },
  { de: '/admin/cancellation-requests', vers: P.annulations },
  { de: '/admin/inclusion-requests', vers: P.inclusion },
  { de: '/admin/bons-cadeaux', vers: P.bonsCadeaux },
  { de: '/admin/ecoles', vers: P.ecoles },
  { de: '/admin/tarifs', vers: P.tarifs },
  { de: '/admin/newsletter', vers: P.newsletter },
  { de: '/admin/reglages-site', vers: P.parametresSite },

  /*
    Trois pages « Fonctionnalité à venir », sans rien derrière. Leurs entrées
    de menu sont désormais inactives, avec leur raison, sur l'accueil de
    l'espace : c'est là que l'ancienne adresse mène. `/admin/parascolaire/
    activities` n'a jamais existé — le menu y pointait (lien mort).
  */
  /*
    Première version du Lot UI 1 (fusionnée par la PR #115, 27/09/2026) :
    Planification s'appelait « Planification & gestion », sous `/admin/gestion`.
    Ses adresses ont pu servir : elles suivent.
  */
  ...Object.values(P).map((vers) => ({ de: vers.replace('/admin/planification', '/admin/gestion'), vers })),

  { de: '/admin/parascolaire', vers: P.accueil },
  { de: '/admin/parascolaire/sessions', vers: P.accueil },
  { de: '/admin/parascolaire/activities', vers: P.accueil },
  { de: '/admin/messaging', vers: P.accueil },
];

/**
 * L'adresse d'arrivée d'une redirection : les paramètres de l'ancienne route
 * reportés dans la nouvelle, puis la chaîne de requête et l'ancre, intactes.
 *
 * Un paramètre attendu mais absent laisse le segment tel quel plutôt que
 * d'inventer une valeur : la page d'arrivée dira « introuvable », ce qui est
 * vrai.
 */
export function cheminRedirige(
  vers: string,
  params: Readonly<Record<string, string | undefined>>,
  search = '',
  hash = '',
): string {
  const chemin = vers.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (motif, nom: string) => {
    const v = params[nom];
    return v === undefined ? motif : encodeURIComponent(v);
  });
  const q = search === '' || search.startsWith('?') ? search : `?${search}`;
  const h = hash === '' || hash.startsWith('#') ? hash : `#${hash}`;
  return `${chemin}${q}${h}`;
}

// ---------------------------------------------------------------------------
//  Pour les tests et les menus
// ---------------------------------------------------------------------------

/** Toutes les adresses canoniques, à plat. */
export function toutesLesPages(): string[] {
  const out: string[] = [];
  const parcourir = (o: unknown) => {
    if (typeof o === 'string') out.push(o);
    else if (o && typeof o === 'object') Object.values(o).forEach(parcourir);
  };
  parcourir(CHEMINS);
  return out;
}
